import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const IncidentsTable = ({ 
  incidents, 
  selectedIncidents, 
  onIncidentSelect, 
  sortConfig, 
  onSort 
}) => {
  const navigate = useNavigate();
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-error text-error-foreground';
      case 'high': return 'bg-warning text-warning-foreground';
      case 'medium': return 'bg-accent text-accent-foreground';
      case 'low': return 'bg-success text-success-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-error/10 text-error border-error/20';
      case 'in-progress': return 'bg-warning/10 text-warning border-warning/20';
      case 'resolved': return 'bg-success/10 text-success border-success/20';
      case 'closed': return 'bg-muted text-muted-foreground border-border';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) {
      return <Icon name="ArrowUpDown" size={14} className="opacity-50" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} />
      : <Icon name="ArrowDown" size={14} />;
  };

  const handleSort = (column) => {
    onSort(column);
  };

  const handleRowClick = (cid) => {
    navigate(`/incident-details?id=${cid}`);
  };

  const handleQuickAction = (e, action, incidentId) => {
    e?.stopPropagation();
    
    switch (action) {
      case 'view':
        navigate(`/incident-details?id=${incidentId}`);
        break;
      case 'edit':
        navigate(`/create-incident?edit=${incidentId}`);
        break;
      case 'escalate':
        // Handle escalation logic
        console.log('Escalating incident:', incidentId);
        break;
      default:
        break;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                  onChange={(e) => {
                    if (e?.target?.checked) {
                      // Select all logic would be handled by parent
                    }
                  }}
                />
              </th>
              
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('id')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  <span>ID</span>
                  {getSortIcon('id')}
                </button>
              </th>
              
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('title')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  <span>Title</span>
                  {getSortIcon('title')}
                </button>
              </th>
              
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  <span>Status</span>
                  {getSortIcon('status')}
                </button>
              </th>
              
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('severity')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  <span>Severity</span>
                  {getSortIcon('severity')}
                </button>
              </th>
              
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('createdAt')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  <span>Created</span>
                  {getSortIcon('createdAt')}
                </button>
              </th>
              
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('assignee')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  <span>Assignee</span>
                  {getSortIcon('assignee')}
                </button>
              </th>
              
              <th className="px-4 py-3 text-left">
                <span className="text-sm font-medium text-foreground">Systems</span>
              </th>
              
              <th className="px-4 py-3 text-right">
                <span className="text-sm font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          
          <tbody className="divide-y divide-border">
            {incidents?.map((incident) => (
              <tr
                key={incident?.id}
                onClick={() => handleRowClick(incident?.cid)}
                className="hover:bg-muted/30 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIncidents?.includes(incident?.id)}
                    onChange={(e) => {
                      e?.stopPropagation();
                      onIncidentSelect(incident?.id, e?.target?.checked);
                    }}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                  />
                </td>
                
                <td className="px-4 py-3">
                  <span className="text-sm font-mono text-primary">#{incident?.id}</span>
                </td>
                
                <td className="px-4 py-3">
                  <div className="max-w-xs">
                    <p className="text-sm font-medium text-foreground truncate">{incident?.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{incident?.description}</p>
                  </div>
                </td>
                
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(incident?.status)}`}>
                    {incident?.status?.replace('-', ' ')?.toUpperCase()}
                  </span>
                </td>
                
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(incident?.severity)}`}>
                    {incident?.severity?.toUpperCase()}
                  </span>
                </td>
                
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground">{formatDate(incident?.createdAt)}</span>
                </td>
                
                <td className="px-4 py-3">
                  {incident?.assignee ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-xs text-primary-foreground font-medium">
                          {incident?.assignee?.split(' ')?.map(n => n?.[0])?.join('')}
                        </span>
                      </div>
                      <span className="text-sm text-foreground">{incident?.assignee}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">Unassigned</span>
                  )}
                </td>
                
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {incident?.impactedSystems?.slice(0, 2)?.map((system, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 text-xs bg-secondary/10 text-primary rounded"
                      >
                        {system}
                      </span>
                    ))}
                    {incident?.impactedSystems?.length > 2 && (
                      <span className="text-xs text-muted-foreground">
                        +{incident?.impactedSystems?.length - 2}
                      </span>
                    )}
                  </div>
                </td>
                
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleQuickAction(e, 'view', incident?.id)}
                      iconName="Eye"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleQuickAction(e, 'edit', incident?.id)}
                      iconName="Edit"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleQuickAction(e, 'escalate', incident?.id)}
                      iconName="TrendingUp"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card Layout */}
      <div className="lg:hidden divide-y divide-border">
        {incidents?.map((incident) => (
          <div
            key={incident?.id}
            onClick={() => handleRowClick(incident?.cid)}
            className="p-4 hover:bg-muted/30 cursor-pointer transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedIncidents?.includes(incident?.id)}
                  onChange={(e) => {
                    e?.stopPropagation();
                    onIncidentSelect(incident?.id, e?.target?.checked);
                  }}
                  className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                />
                <span className="text-sm font-mono text-primary">#{incident?.id}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(incident?.severity)}`}>
                  {incident?.severity?.toUpperCase()}
                </span>
                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(incident?.status)}`}>
                  {incident?.status?.replace('-', ' ')?.toUpperCase()}
                </span>
              </div>
            </div>
            
            <h3 className="text-sm font-medium text-foreground mb-2">{incident?.title}</h3>
            <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{incident?.description}</p>
            
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
              <span>{formatDate(incident?.createdAt)}</span>
              <span>{incident?.assignee || 'Unassigned'}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-1">
                {incident?.impactedSystems?.slice(0, 2)?.map((system, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 text-xs bg-secondary/10 text-secondary rounded"
                  >
                    {system}
                  </span>
                ))}
                {incident?.impactedSystems?.length > 2 && (
                  <span className="text-xs text-muted-foreground">
                    +{incident?.impactedSystems?.length - 2}
                  </span>
                )}
              </div>
              
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleQuickAction(e, 'view', incident?.id)}
                  iconName="Eye"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleQuickAction(e, 'edit', incident?.id)}
                  iconName="Edit"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleQuickAction(e, 'escalate', incident?.id)}
                  iconName="TrendingUp"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IncidentsTable;